import React from 'react';
import { useIntl } from 'react-intl';
import { Tooltip } from 'antd';
import dayjs from 'dayjs';
import { MparticleSegmentReadiness, SegmentSyncReason } from 'core/mparticle/types';
import { getSegmentDisplayStatus } from 'core/mparticle/readinessStatus';

type SegmentUsersCountType = {
  customersCount: number;
  readiness?: MparticleSegmentReadiness;
  isMparticleSegment: boolean;
};

const daysSince = (date: string | null): number => (date ? dayjs().diff(dayjs(date), 'day') : 0);

const formatDateTime = (date: string | null): string => (date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '—');

// Human label for each backend sync reason, appended in parentheses to the synchronizing tooltip.
const reasonMessageId: Record<SegmentSyncReason, string> = {
  waiting: 'waiting for data',
  error: 'sync error',
  empty: 'empty audience',
};

const SegmentUsersCount: React.FC<SegmentUsersCountType> = ({ customersCount, readiness, isMparticleSegment }) => {
  const intl = useIntl();

  if (!isMparticleSegment) {
    return <p>{customersCount}</p>;
  }

  const status = getSegmentDisplayStatus(readiness);
  const membershipCount = readiness?.membershipCount ?? 0;

  if (status === 'ready') {
    return <p>{membershipCount.toLocaleString()}</p>;
  }

  if (status === 'stale') {
    return (
      <p className="segment_count_wrapper">
        {membershipCount.toLocaleString()}
        <Tooltip
          placement="top"
          title={intl.formatMessage(
            { id: 'Last updated {days} days ago. The audience may be disconnected in mParticle.' },
            { days: daysSince(readiness?.lastIngestedAt ?? null) },
          )}
        >
          <span className="segment_badge segment_badge--stale">{intl.formatMessage({ id: 'Outdated' })}</span>
        </Tooltip>
      </p>
    );
  }

  if (status === 'stuck') {
    return (
      <p className="segment_count_wrapper">
        <Tooltip
          placement="top"
          title={intl.formatMessage(
            {
              id:
                '⚠️ Stuck · {attempts} attempts without valid data. Needs review (audience with no members ' +
                'or no connected output). Still retried daily.',
            },
            { attempts: readiness?.consecutiveFailures ?? 0 },
          )}
        >
          <span className="segment_badge segment_badge--stuck">{intl.formatMessage({ id: 'Stuck' })}</span>
        </Tooltip>
      </p>
    );
  }

  const syncReason = readiness?.syncReason ?? null;
  const reasonSuffix = syncReason ? ` (${intl.formatMessage({ id: reasonMessageId[syncReason] })})` : '';

  return (
    <p className="segment_count_wrapper">
      <Tooltip
        placement="top"
        title={intl.formatMessage(
          {
            id:
              'Synchronizing · waiting for valid data. In this state since {since} · Last attempt ' +
              '{attempt}{reason}. Retried daily.',
          },
          {
            since: formatDateTime(readiness?.synchronizingSince ?? null),
            attempt: formatDateTime(readiness?.lastAttemptAt ?? null),
            reason: reasonSuffix,
          },
        )}
      >
        <span className="segment_badge segment_badge--synchronizing">
          {intl.formatMessage({ id: 'Synchronizing' })}
        </span>
      </Tooltip>
    </p>
  );
};

SegmentUsersCount.defaultProps = {
  readiness: undefined,
};

export default SegmentUsersCount;
